import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ComponentMessageFileType, ComponentMessageType } from "../../../types/ComponentMessageType";
import { MessageFile } from "./File";
import styles from "./QuotedMessage.module.scss";
import { IoLocation } from "react-icons/io5";
import classNames from "classnames";

export const QuotedMessage: React.FC<{
  message: string;
  senderName: string;
  onClick?: () => void;
  file?: ComponentMessageFileType;
  timeContrast: boolean;
  testid?: string;
  location?: ComponentMessageType["location"];
}> = ({ message, senderName, onClick, file, timeContrast, testid, location }) => {
  const { t } = useTranslation();
  const [fileElement, littleFileIcon] = useMemo(() => {
    if (!file) return [<></>, undefined];
    if (file.fileType === "audio" || file.fileType === "call_recording")
      return [undefined, <SvgV2 className={styles.fileIcon} width={12.58} height={16.85} iconName={"whatsapp-microphone"} />];
    if (file.fileType === "video" && !file.previewUrl) {
      return [undefined, <SvgV2 className={styles.fileIcon} width={18} height={12} iconName={"whatsapp-video-camera"} />];
    }
    if (file.fileType === "image") {
      return [
        <MessageFile file={file} withTail={false} timeContrast={timeContrast} />,
        <SvgV2 className={styles.fileIcon} width={15} height={13.5} iconName={"whatsapp-camera"} />,
      ];
    }
    if (file.fileType === "video") {
      return [
        <MessageFile file={file} withTail={false} timeContrast={timeContrast} />,
        <SvgV2 className={styles.fileIcon} width={18} height={12} iconName={"whatsapp-video-camera"} />,
      ];
    }

    return [undefined, <SvgV2 className={styles.fileIcon} width={12} height={14} iconName={"whatsapp-quoted-file"} />];
  }, [file, location, message, timeContrast]);

  const formattedMessage = useMemo(() => {
    if (location)
      return (
        <span className={styles.quotedLocation}>
          <IoLocation className={styles.fileIcon} width={12.58} height={16.85} />{" "}
          <span>{t("whatsapp_business_api_agent_page.upload_types.location")}</span>
        </span>
      );
    if (message || !file) {
      return message;
    }
    if (file.fileType === "audio" || file.fileType === "call_recording") {
      return <span>{t("chat_component.voice_message")}</span>;
    } else {
      return <span>{file.title}</span>;
    }
  }, [file, location, message, t]);

  return (
    <div
      className={classNames(styles.quotedMessageContainer, { [styles.locationMessageContainer]: Boolean(location) })}
      onClick={() => onClick?.()}
      data-testid={`quoted-msg-${testid}`}
    >
      <div className={styles.quotedMessageBody}>
        <div className={styles.qoutedMessageName} data-testid={`quoted-msg-sender-name-${testid}`}>
          {senderName}
        </div>
        <div className={styles.quotedMessageText} data-testid={`quoted-msg-message-${testid}`}>
          {littleFileIcon} {formattedMessage}
        </div>
      </div>
      {(location || (file && file.fileType !== "audio")) && (
        <div
          className={classNames(styles.quotedMessageFileContainer, {
            [styles.locationMessage]: Boolean(location),
            [styles.imageOrVideoContainer]: ["image", "video"].includes(file?.fileType || ""),
          })}
        >
          {fileElement}
        </div>
      )}
    </div>
  );
};
